import useAgency from 'features/Agency/useAgency';
import ChargeConnectorCardView from 'features/Charge/ChargeConnector/components/ChargeConnectorCardView';
import ChargeConnectorListView from 'features/Charge/ChargeConnector/components/ChargeConnectorListView';
import DialogAddChargeConnectorView from 'features/Charge/ChargeConnector/components/DialogAddChargeConnectorView';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import ChargeTransactionCompactListView from 'features/Charge/ChargeTransaction/components/ChargeTransactionCompactListView';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import DashboardChartCard from 'general/components/quybka/Cards/DashboardChartCard';
import BarChart from 'general/components/quybka/Charts/BarChart';
import AppColors from 'general/constants/AppColors';
import AppConstants from 'general/constants/AppConstants';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ShowMoreText from 'react-show-more-text';

ChargePointDetailView.propTypes = {
  chargePointId: PropTypes.string,
};

function ChargePointDetailView(props) {
  // MARK: ---- PROPERTIES ----
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const router = useRouter();
  const prefKey = useMemo(() => {
    const userId = user.userId;
    const pathName = router.pathname;
    const key = `${userId}_${pathName}`;
    return key;
  }, [user, router]);
  const { operatorList } = useAgency();
  const { chargePointId } = props;
  const [chargePointDetail, setChargePointDetail] = useState(null);
  useEffect(() => {
    ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
      setChargePointDetail(chargePoint);
    });
  }, [chargePointId]);
  const [chargePricing, setChargePricing] = useState(null);
  useEffect(() => {
    const pricingId = chargePointDetail?.pricing?.pricingId;
    if (pricingId) {
      ChargeHelper.API.requestGetChargePricingDetail(pricingId).then((pricing) => {
        setChargePricing(pricing);
      });
    }
  }, [chargePointDetail]);
  const { labels, data } = Utils.generateRandomChartData(7, 0, 400);
  const [filterChargeTransaction, setFilterChargeTransaction] = useState({
    pointId: chargePointId,
  });
  const chargeTransactionListViewEl = useMemo(() => {
    return (
      <ChargeTransactionCompactListView prefKey={prefKey} initialFilter={filterChargeTransaction} />
    );
  }, [prefKey, filterChargeTransaction]);
  const [chartData, setChartData] = useState({});
  const [chartFilter, setChartFilter] = useState({
    from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
    to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
    dateFilter: dateFilter.baMuoiNgayGanNhat,
  });
  useEffect(() => {
    // lay du lieu chart
    ChargeHelper.API.Dashboard.requestGetPointStatistics({
      pointId: chargePointId,
      from: chartFilter.from,
      to: chartFilter.to,
    }).then((res) => {
      if (_.isObject(res)) setChartData(res);
    });
  }, [chargePointId, chartFilter]);
  // dien sac theo ngay
  const chartEnergyByDateEl = useMemo(() => {
    const energyByDate = chartData?.dailyEnergy?.dailyEnergy ?? [];
    let labels = energyByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = energyByDate.map((item) => ((item.energy ?? 0) / 1000).toFixed(3));

    if (energyByDate.length > 31) {
      const groupedData = _.groupBy(energyByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.energy, 0);
        return (total / 1000).toFixed(3); // Chuyển đổi sang kWh
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê điện sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' kWh'
        }
        valueColor="rgba(41, 132, 238, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Điện sạc"
            unit="kWh"
            labels={labels}
            data={data}
            color="rgba(41, 132, 238, 0.4)"
            hoverColor="rgba(41, 132, 238, 1)"
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  // doanh thu theo ngay
  const chartRevenueByDateEl = useMemo(() => {
    const revenueByDate = chartData?.dailyRevenue?.dailyRevenue ?? [];
    let labels = revenueByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = revenueByDate.map((item) => (item.revenue ?? 0) / 1000000);

    if (revenueByDate.length > 31) {
      const groupedData = _.groupBy(revenueByDate, (item) => moment(item.date).format('MM/YYYY'));
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.revenue, 0);
        return (total / 1000000).toFixed(2); // Chuyển đổi sang triệu
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê doanh thu"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' triệu'
        }
        valueColor="rgba(48, 189, 111, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Doanh thu"
            unit="triệu"
            labels={labels}
            data={data}
            color="rgba(48, 189, 111, 0.4)"
            hoverColor="rgba(48, 189, 111, 1)"
            showLine={true}
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  // phien sac theo ngay
  const chartTransactionByDateEl = useMemo(() => {
    const transactionByDate = chartData?.dailyTransaction?.dailyTransaction ?? [];
    let labels = transactionByDate.map((item) => Utils.formatDateTimeToString(item.date, 'DD'));
    let data = transactionByDate.map((item) => item.transactions ?? 0);

    if (transactionByDate.length > 31) {
      const groupedData = _.groupBy(transactionByDate, (item) =>
        moment(item.date).format('MM/YYYY')
      );
      labels = Object.keys(groupedData);
      data = labels.map((label) => {
        const total = groupedData[label].reduce((sum, item) => sum + item.transactions, 0);
        return total;
      });
    }

    return (
      <DashboardChartCard
        title="Thống kê phiên sạc"
        name="Tổng"
        value={
          Utils.formatNumber(data.reduce((sum, item) => _.toNumber(sum) + _.toNumber(item), 0)) +
          ' phiên sạc'
        }
        valueColor="rgba(88, 86, 214, 1)"
        actionEl={
          <CustomDateRangePicker
            alignRight={true}
            toggleFontWeight="600"
            toggleElementBorderRadius={10}
            initialValue={chartFilter.dateFilter}
            onPressSave={(selectedValue, startDate, endDate) => {
              setChartFilter({
                from: startDate,
                to: endDate,
                dateFilter: selectedValue,
              });
            }}
          />
        }
        chartEl={
          <BarChart
            title="Phiên sạc"
            unit="phiên sạc"
            labels={labels}
            data={data}
            color="rgba(88, 86, 214, 0.4)"
            hoverColor="rgba(88, 86, 214, 1)"
          />
        }
      />
    );
  }, [chartData, chartFilter]);
  console.log({ chargePointDetail });

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    if (!row) return [];

    const actions = [];
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointUpdate.code)) {
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          router.navigate('edit');
        },
      });
      // hoan thanh bao tri
      if (row.status === ChargeConstants.ChargePointStatus.MAINTAINING) {
        actions.push({
          text: 'Hoàn thành bảo trì',
          iconElement: (
            <i className="fa-regular fa-clipboard-check" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmCompleteMaintaining(row, () => {
              ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
                setChargePointDetail(chargePoint);
              });
            });
          },
        });
      }
      // thiet lap lich bao tri dinh ky
      actions.push({
        text: 'Thiết lập lịch bảo trì định kỳ',
        iconElement: (
          <i className="fa-regular fa-screwdriver-wrench" style={{ fontSize: '1.1rem' }} />
        ),
        click: () => {
          MaintainerHelper.UI.showDialogCreateMaintenanceSchedule(row, () => {
            ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
              setChargePointDetail(chargePoint);
            });
          });
        },
      });
      // di chuyen tru sac
      actions.push({
        text: 'Di chuyển trụ sạc',
        iconElement: (
          <i className="fa-regular fa-arrow-right-arrow-left" style={{ fontSize: '1.1rem' }} />
        ),
        click: () => {
          ChargeHelper.UI.showDialogConfirmMoveChargePoint(row, () => {
            ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
              setChargePointDetail(chargePoint);
            });
          });
        },
      });
      actions.push({
        text: 'Khởi động lại trụ sạc',
        iconElement: <i className="fa-regular fa-rotate-right" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmRestartChargePoint(row, () => {
            // khoi dong lai tru sac thanh cong
            ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
              setChargePointDetail(chargePoint);
            });
          });
        },
      });
      actions.push({
        text: 'Giới hạn công suất',
        iconElement: (
          <i className="fa-regular fa-arrows-from-dotted-line" style={{ fontSize: '1.1rem' }} />
        ),
        click: () => {
          ChargeHelper.UI.showDialogConfirmSetPowerLimitChargePoint(row, () => {
            // set gioi han cong suat thanh cong
            ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
              setChargePointDetail(chargePoint);
            });
          });
        },
      });
      if (row.status !== ChargeConstants.ChargePointStatus.FAULT) {
        actions.push({
          text: 'Báo lỗi trụ sạc',
          iconElement: (
            <i className="fa-regular fa-triangle-exclamation" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            ChargeHelper.UI.showDialogConfirmReportErrorChargePoint(row, () => {
              // bao loi tru sac thanh cong
              ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
                setChargePointDetail(chargePoint);
              });
            });
          },
        });
      }
      if (
        [
          ChargeConstants.ChargePointStatus.FAULT,
          ChargeConstants.ChargePointStatus.MAINTAINING,
        ].includes(row.status)
      ) {
        actions.push({
          text: 'Khôi phục trụ sạc',
          iconElement: <i className="fa-regular fa-play" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            ChargeHelper.UI.showDialogConfirmRestoreChargePoint(row, () => {
              // khoi phuc tru sac thanh cong
              ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
                setChargePointDetail(chargePoint);
              });
            });
          },
        });
      }
    }
    if (Utils.checkPermission(permissionList, AppConstants.permissionCode.chargePointDelete.code)) {
      actions.push({
        text: 'Xoá',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          ChargeHelper.UI.showDialogConfirmDeleteChargePoint(row, () => {
            // xoa tru sac thanh cong
            router.navigate(-1);
          });
        },
      });
    }
    return actions;
  };

  const handleAddChargeConnector = (row) => {
    AppCustomDialogHelper.show(
      row ? 'Chỉnh sửa vòi sạc' : 'Thêm vòi sạc',
      <DialogAddChargeConnectorView
        chargePointDetail={chargePointDetail}
        item={row}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
        onDone={() => {
          AppCustomDialogHelper.hide();
          ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
            setChargePointDetail(chargePoint);
          });
        }}
      />,
      'md',
      false,
      true
    );
  };

  return (
    <div>
      {/* mat ket noi tru sac */}
      {chargePointDetail?.status === 'DISCONNECTED' && (
        <div className="px-4 mt-4">
          {UIHelper.buildAlert(
            <i className="fa-duotone fa-circle-info fa-2x" style={{ color: AppColors.danger }} />,
            'Mất kết nối trụ sạc',
            'Trong thời gian mất kết nối các chức năng điều khiển trụ sạc sẽ bị vô hiệu hóa',
            AppColors.danger,
            'rgba(255, 246, 246, 1)'
          )}
        </div>
      )}

      {/* thong tin tru sac */}
      <div className="row p-0 m-0 px-2 mt-4">
        <div className="col-lg-12 mb-4 px-2">
          {UIHelper.buildCard(
            <div>
              {/* tieu de */}
              <div className="d-flex align-items-center justify-content-between px-4 py-3">
                <div className="flex-fill">
                  <div className="d-flex align-items-center gap-3">
                    {/* avatar */}
                    <div className="">
                      {UIHelper.buildAvatar(
                        chargePointDetail?.image
                          ? Utils.getFullUrl(chargePointDetail?.image)
                          : null,
                        44,
                        AppResource.images.chargeConnectorTypes.imgChargePointDefault
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <div className="d-flex align-items-center gap-2">
                        <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                          {chargePointDetail?.name?.toUpperCase()}
                        </span>
                        <KTLabel
                          text={ChargeHelper.Utils.getChargePointStatusName(
                            chargePointDetail?.status
                          )}
                          isInline={true}
                          size={KTLabelSize.medium}
                          additionalClassName={`label-light-${ChargeHelper.Utils.getChargePointStatusColor(
                            chargePointDetail?.status
                          )}`}
                        />
                      </div>
                      <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                        Địa chỉ:{' '}
                        <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                          {ChargeHelper.Utils.getChargeStationFullAddress(
                            chargePointDetail?.chargeStation
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  {UIHelper.buildMoreActionsButton(buildMoreActions(chargePointDetail))}
                </div>
              </div>

              <div className="row m-0 p-0 px-2">
                {/* thong tin tru sac */}
                <div className="col-lg-4 m-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin trụ sạc')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('Mã trụ sạc', chargePointDetail?.pointCode)}
                        {UIHelper.buildCardCellItem(
                          'Loại phương tiện',
                          chargePointDetail?.chargeModel?.vehicleType
                            ? ChargeHelper.Data.vehicleTypeOptions.find(
                              (item) => item.value === chargePointDetail?.chargeModel?.vehicleType
                            )?.name
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Trạm sạc',
                          chargePointDetail?.chargeStation?.name,
                          () => {
                            Utils.openInNewTab(`${AppRoute.stationList}/${chargePointDetail?.chargeStation?.stationId ?? ''}`)
                          }
                        )}
                        {UIHelper.buildCardCellItem(
                          'Nhà điều hành',
                          _.find(
                            operatorList,
                            (item) => item.operatorId === chargePointDetail?.operatorId
                          )?.name
                        )}
                        {/* {UIHelper.buildCardCellItem(
                          'Số điện thoại',
                          chargePointDetail?.phoneNumber
                        )} */}
                        {UIHelper.buildCardCellItem('Loại trụ sạc', chargePointDetail?.type)}
                        {UIHelper.buildCardCellItem(
                          'Dòng trụ sạc',
                          chargePointDetail?.chargeModel?.name,
                          () => { }
                        )}
                        {UIHelper.buildCardCellItem(
                          'Công suất tối đa',
                          chargePointDetail?.chargeModel?.power
                            ? _.toNumber(chargePointDetail?.chargeModel?.power) / 1000 + 'kW'
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Điện áp',
                          chargePointDetail?.chargeModel?.voltage
                            ? chargePointDetail?.chargeModel?.voltage + 'V'
                            : ''
                        )}
                        {/* {UIHelper.buildCardCellItem(
                          'Dòng điện tối đa',
                          chargePointDetail?.maxAmpere ? chargePointDetail?.maxAmpere + 'A' : ''
                        )} */}
                        {UIHelper.buildCardCellItem(
                          'Giới hạn công suất',
                          !_.isNull(chargePointDetail?.powerLimit)
                            ? (chargePointDetail?.powerLimit / 1000).toFixed(3) + 'kW'
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Số Serial',
                          chargePointDetail?.serialNumber,
                          null,
                          false
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* thong tin ket noi */}
                <div className="col-lg-4 m-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin kết nối')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('OCPP ID', chargePointDetail?.ocppId)}
                        {UIHelper.buildCardCellItem('Giao thức', chargePointDetail?.protocol)}
                        {UIHelper.buildCardCellItem('Địa chỉ IP', chargePointDetail?.ipAddress)}
                        {UIHelper.buildCardCellItem('IMSI', chargePointDetail?.imsi)}
                        {UIHelper.buildCardCellItem(
                          'Phiên bản Firmware',
                          chargePointDetail?.firmwareVersion,
                          null,
                          false
                        )}
                      </div>
                    )}
                  </div>
                  {/* gia sac dien */}
                  <div className='mt-4'>
                    {UIHelper.buildSectionTitle('Giá sạc điện')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem(
                          'Phương thức tính giá',
                          _.find(
                            ChargeHelper.Data.chargePointPricingModeOptions,
                            (item) => item.value === chargePricing?.pricingMode
                          )?.name
                        )}
                        {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.FLAT &&
                          UIHelper.buildCardCellItem(
                            'Đơn giá sạc xe',
                            chargePricing?.flatPrice
                              ? Utils.formatNumber(chargePricing?.flatPrice) + ' VNĐ/kWh'
                              : ''
                          )}
                        {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS &&
                          UIHelper.buildCardCellItem(
                            'Giá giờ bình thường',
                            chargePricing?.normalPrice
                              ? Utils.formatNumber(chargePricing?.normalPrice) + ' VNĐ/kWh'
                              : ''
                          )}
                        {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS &&
                          UIHelper.buildCardCellItem(
                            'Giá giờ cao điểm',
                            chargePricing?.peakPrice
                              ? Utils.formatNumber(chargePricing?.peakPrice) + ' VNĐ/kWh'
                              : ''
                          )}
                        {chargePricing?.pricingMode === ChargeConstants.ChargePricingMode.HOURS &&
                          UIHelper.buildCardCellItem(
                            'Giá giờ thấp điểm',
                            chargePricing?.offPeakPrice
                              ? Utils.formatNumber(chargePricing?.offPeakPrice) + ' VNĐ/kWh'
                              : ''
                          )}
                        {UIHelper.buildCardCellItem(
                          'Thời gian chờ miễn phí',
                          chargePricing?.freeOverchargeDuration
                            ? Utils.convertSecondsToMS(chargePricing?.freeOverchargeDuration)
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Đơn giá sạc quá giờ',
                          chargePricing?.overchargePrice
                            ? Utils.formatNumber(chargePricing?.overchargePrice) + ' VNĐ/phút'
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Giá đặt giữ chỗ',
                          chargePricing?.reservationFee
                            ? Utils.formatNumber(chargePricing?.reservationFee ?? 0) + ' VNĐ'
                            : ''
                        )}
                        {UIHelper.buildCardCellItem(
                          'Người định giá',
                          chargePricing?.user?.fullname,
                          () => { },
                          false
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {/* thong tin sim */}
                <div className="col-lg-4 m-0 px-2 mb-4">
                  <div>
                    {UIHelper.buildSectionTitle('Thông tin SIM')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('Số điện thoại', chargePointDetail?.phoneNumber)}
                        {UIHelper.buildCardCellItem('Số serial SIM', chargePointDetail?.iccid)}
                        {UIHelper.buildCardCellItem('Nhà mạng', chargePointDetail?.networkOperator)}
                        {UIHelper.buildCardCellItem('Gói cước', chargePointDetail?.dataPackage)}
                        {UIHelper.buildCardCellItem(
                          'HSD gói cước',
                          Utils.formatDateTimeToString(chargePointDetail?.dataPackageExpire, 'DD/MM/YYYY'),
                          null,
                          false
                        )}
                      </div>
                    )}
                  </div>
                  <div className='mt-4'>
                    {UIHelper.buildSectionTitle('Lịch bảo trì định kỳ')}
                    {UIHelper.buildSectionCard(
                      <div>
                        {UIHelper.buildCardCellItem('Ngày bắt đầu', Utils.formatDateTimeToString(chargePointDetail?.maintenanceSchedule?.startDate, 'DD/MM/YYYY'))}
                        {UIHelper.buildCardCellItem('Chu kỳ', Utils.formatNumber(chargePointDetail?.maintenanceSchedule?.cycle) + ' ngày', null, false)}
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    {UIHelper.buildSectionTitle('Mô tả lịch bảo trì')}
                    {UIHelper.buildSectionCard(
                      <div
                        className="px-3 py-2"
                        style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}
                      >
                        <ShowMoreText
                          lines={3}
                          more="Xem thêm"
                          less="Thu gọn"
                          className="text-break"
                          anchorClass="text-success font-size-normal font-weight-bolder cursor-pointer hover-opacity-60"
                          onClick={() => { }}
                          expanded={false}
                          truncatedEndingComponent={'... '}
                        >
                          {chargePointDetail?.maintenanceSchedule?.description}
                        </ShowMoreText>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            true,
            false,
            true,
            false,
            chargePointDetail?.status === 'DISCONNECTED' ? AppColors.danger : AppColors.gray[300]
          )}
        </div>
      </div>

      {/* thong tin voi sac */}
      <div className="row p-0 m-0 px-0">
        <div className="col-lg-12 mb-4 px-0">
          {chargePointDetail?.chargeModel?.vehicleType === ChargeConstants.VehicleType.CAR ? (
            <div className="px-4">
              {UIHelper.buildCard(
                <div>
                  {UIHelper.buildCardHeader('Thông tin vòi sạc', false, null, true)}
                  <div className="row p-0 m-0 px-2">
                    {chargePointDetail?.chargeConnectors?.length > 0 ? (
                      chargePointDetail?.chargeConnectors?.map((connector) => (
                        <div className="col-xl-6 mb-4 px-2" key={connector.connectorId}>
                          <ChargeConnectorCardView
                            chargePointDetail={chargePointDetail}
                            chargeConnectorDetail={connector}
                            onRefresh={() => {
                              ChargeHelper.API.requestGetChargePointDetail(chargePointId).then(
                                (chargePoint) => {
                                  setChargePointDetail(chargePoint);
                                  setFilterChargeTransaction({ ...filterChargeTransaction });
                                }
                              );
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="">
                        {UIHelper.buildEmptyNoData('Trụ sạc này không chứa vòi sạc nào.')}
                      </div>
                    )}
                    {chargePointDetail?.chargeConnectors?.length < 2 && (
                      <div
                        className={`${chargePointDetail?.chargeConnectors?.length == 0
                          ? 'col-xl-12'
                          : 'col-xl-6'
                          } mb-4 px-2 align-items-center d-flex justify-content-center`}
                      >
                        <div>
                          {UIHelper.buildGreenButton(
                            'Thêm vòi sạc',
                            () => {
                              handleAddChargeConnector();
                            },
                            <i className="fa-regular fa-circle-plus" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <ChargeConnectorListView
              chargePointDetail={chargePointDetail}
              items={chargePointDetail?.chargeConnectors}
              onRefresh={() => {
                ChargeHelper.API.requestGetChargePointDetail(chargePointId).then((chargePoint) => {
                  setChargePointDetail(chargePoint);
                  setFilterChargeTransaction({ ...filterChargeTransaction });
                });
              }}
            />
          )}
        </div>
      </div>

      {/* danh sach phien sac */}
      <div className="px-4">{chargeTransactionListViewEl}</div>

      {/* bieu do thong ke */}
      {/* bieu do thong ke */}
      <div className="mt-4">
        <div className="row m-0 p-0 px-2">
          {/* thong ke dien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartEnergyByDateEl}</div>
          {/* thong ke phien sac */}
          <div className="col-lg-6 px-2 mb-4">{chartTransactionByDateEl}</div>
          {/* thong ke doanh thu */}
          <div className="col-lg-6 px-2 mb-4">{chartRevenueByDateEl}</div>
        </div>
      </div>
    </div>
  );
}

export default ChargePointDetailView;
